import heic2any from "heic2any";

export const normalizeImageFile = async (file, options = {}) => {
  const {
    maxWidth = 1600,
    maxHeight = 1600,
    quality = 0.7,
  } = options;

  const mimeType = (file.type || "").toLowerCase();
  const fileName = file.name || "";

  const isHeicMime =
    mimeType === "image/heic" ||
    mimeType === "image/heif";

  const isHeicExtension = /\.hei(c|f)$/i.test(fileName);

  let sourceBlob = file;
  let outputName = fileName;

  // Only attempt HEIC conversion if MIME type really says HEIC/HEIF.
  // Extension alone is not trustworthy.
  if (isHeicMime) {
    const converted = await heic2any({
      blob: file,
      toType: "image/jpeg",
    });

    sourceBlob = Array.isArray(converted) ? converted[0] : converted;
    outputName = fileName.replace(/\.[^/.]+$/, ".jpg");
  }

  if (!(sourceBlob instanceof Blob)) {
    throw new Error("Image conversion did not return a valid Blob.");
  }

  const imageUrl = URL.createObjectURL(sourceBlob);

  try {
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("Failed to load image."));
      image.src = imageUrl;
    });

    const width = img.width;
    const height = img.height;

    if (!width || !height) {
      throw new Error("Image has invalid dimensions.");
    }

    const scale = Math.min(maxWidth / width, maxHeight / height, 1);
    const newWidth = Math.round(width * scale);
    const newHeight = Math.round(height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = newWidth;
    canvas.height = newHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Could not create canvas context.");
    }

    ctx.drawImage(img, 0, 0, newWidth, newHeight);

    const resizedBlob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Failed to create compressed image."));
            return;
          }
          resolve(blob);
        },
        "image/jpeg",
        quality
      );
    });

    return new File(
      [resizedBlob],
      outputName.replace(/\.[^/.]+$/, ".jpg"),
      { type: "image/jpeg" }
    );
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
};